import { NOT_ASSIGNED } from 'constants/constants';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { DataQualityAssessmentItemEnum } from 'services/EpdClient';
import styled from 'styled-components';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { DataQualityAssessmentItemModel } from 'types/types';
import { formatDate } from 'util/utils';

import { cellTotalPercentTemplate } from '../GridService';
import { DataTableStyled } from '../StyledGrid';
import usePcrV2 from '../../hooks/usePcrV2';

type TProps = {
  dataQualityAssessmentItems: DataQualityAssessmentItemModel[] | undefined;
  className?: string;
};

type LabelsAccordingToPCRModel = {
  dataQualityAssessmentTitle: string;
  dataQualityAssessmentDisclaimer: string;
};

const DataQualityAssessmentAndReferenceYearsGridPdf = ({ dataQualityAssessmentItems, className }: TProps) => {
  const isPcrV2 = usePcrV2();

  const labelsAccordingToPCR: LabelsAccordingToPCRModel = isPcrV2
    ? {
        dataQualityAssessmentTitle: 'Data quality assessment',
        dataQualityAssessmentDisclaimer:
          'The share of primary data is calculated based on GWP-GHG results. It is a simplified indicator for data quality that supports the use of more primary data to increase the representativeness of and comparability between EPDs. Note that the indicator does not capture all relevant aspects of data quality and is not comparable across product categories.',
      }
    : {
        dataQualityAssessmentTitle: 'Data quality assessment and reference years',
        dataQualityAssessmentDisclaimer:
          'The share of primary data is calculated based on GWP-GHG results. It is a simplified indicator for data quality that do not capture all relevant aspects of data quality. The indicator is not comparable across product categories.',
      };

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-start">
      <HeaderCaptionSemiBold className="white-space-nowrap">
        {labelsAccordingToPCR.dataQualityAssessmentTitle}
      </HeaderCaptionSemiBold>
    </div>
  );

  const valueBodyTemplate = (fieldName: string, rowData: DataQualityAssessmentItemModel) => {
    let value;
    switch (fieldName) {
      case DataQualityAssessmentItemEnum.ProcessName:
        return rowData.processName;
      case DataQualityAssessmentItemEnum.SourceType:
        return rowData.sourceType;
      case DataQualityAssessmentItemEnum.Source:
        return rowData.source;
      case DataQualityAssessmentItemEnum.ReferenceYear:
        value = isPcrV2
          ? rowData.referenceYear
          : rowData.referenceYearStartDate &&
            rowData.referenceYearEndDate &&
            (`${formatDate(rowData.referenceYearStartDate)} - ${formatDate(rowData.referenceYearEndDate)}` as
              | string
              | undefined);
        return value;
      case DataQualityAssessmentItemEnum.DataCategory:
        return rowData.dataCategory;
      case DataQualityAssessmentItemEnum.ShareOfPrimaryData:
        value = rowData.shareOfPrimaryData as number | undefined;
        return value !== null && value !== undefined ? `${value}%` : '';
      default:
        return NOT_ASSIGNED;
    }
  };

  const footerGroup = (
    <ColumnGroup>
      <Row style={{ borderTop: 'none' }}>
        <Column
          className="p-row-note"
          footer={<strong>Total share of primary data, of GWP-GHG results for A1-A3</strong>}
          colSpan={5}
        />
        <Column
          className="p-row-total"
          footer={cellTotalPercentTemplate('shareOfPrimaryData', dataQualityAssessmentItems, undefined)}
        />
      </Row>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" footer={<div>Note</div>} />
        <Column
          className="p-row-note"
          footer={<span>{labelsAccordingToPCR.dataQualityAssessmentDisclaimer}</span>}
          colSpan={5}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      <QualityAssessmentDataTableStyled
        style={{ overflow: 'auto', display: 'grid' }}
        tableStyle={{ minWidth: '49rem' }}
        className={className}
        header={header}
        footerColumnGroup={footerGroup}
        value={dataQualityAssessmentItems}
        dataKey="id"
        showGridlines
      >
        <Column
          field="processName"
          header={'Process name'}
          className="padding"
          body={(rowData) => valueBodyTemplate('processName', rowData)}
        />
        <Column
          field="sourceType"
          header="Source type"
          className="padding"
          body={(rowData) => valueBodyTemplate('sourceType', rowData)}
        />
        <Column
          field="source"
          header="Source"
          className="padding"
          body={(rowData) => valueBodyTemplate('source', rowData)}
        />
        <Column
          field="referenceYear"
          header="Reference year"
          className="padding"
          body={(rowData) => valueBodyTemplate('referenceYear', rowData)}
        />
        <Column
          field="dataCategory"
          header="Data category"
          className="padding"
          body={(rowData) => valueBodyTemplate('dataCategory', rowData)}
        />
        <Column
          field="shareOfPrimaryData"
          header="Share of primary data, of GWP-GHG results for A1-A3"
          className="padding"
          body={(rowData) => valueBodyTemplate('shareOfPrimaryData', rowData)}
        />
      </QualityAssessmentDataTableStyled>
    </div>
  );
};

const QualityAssessmentDataTableStyled = styled(DataTableStyled)`
  border-collapse: collapse;

  .p-datatable-table {
    height: 100%;
  }
  .p-datatable-tbody > tr > td {
    padding: 0;
  }
  .p-datatable-tbody > tr > td.padding,
  .p-datatable-tbody > tr.p-datatable-emptymessage > td {
    padding: 0.5rem 1rem;
  }
  && .p-inputtext.p-dropdown-label.p-placeholder {
    color: ${(props) => props.theme.colors.primaryBlack};
  }
  .p-checkbox.p-component.p-disabled > .p-checkbox-box.p-component {
    opacity: 1;
    border-color: ${(props) => props.theme.colors.elementsDropDown};
  }
`;

export default DataQualityAssessmentAndReferenceYearsGridPdf;
